// frontend/src/pages/admin/AdminAuctionPage.js
//
// Super admin: Super Series player auction — SETUP (part 1).
// Design agreed with SB, 30 Oct 2026. Backend: routes/auction.js.
//
//   1. Pick the series and make it an auction series.
//   2. Rules: purse, base price, bid ladder, retention cuts (charter defaults).
//   3. Teams: name, owner (in his own team at ₹0), captain, purse after retentions.
//   4. Last year's squads: paste / check the list, match every name to a member.
//   5. Player pool: members with a YES for any date + guests. Squads are always
//      equal (pool ÷ 3). Retain up to 3 per team, leave players out, add guests.
//
// Colours and type follow the UCC site (navy / gold, Rajdhani headings).

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../utils/api';

const NAVY = '#1E3A6E', GOLD = '#F5C518', GOLD_D = '#C9940F', MUTED = '#6B7280', BORDER = '#E5E7EB';
const H = { fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, color: NAVY };

// Western Living UCC Super Series 2025 — as SB sent it (2 Nov 2026).
const SQUADS_2025 = `UCC Strikers
Abhishek Arora (Jimmy)
Ajay Sood
Amit Bansal
Sunny Bhalla
Gaurav Berry (Guest)
Kapil Kalra
Naresh Chopra
Puneet Sareen
Raghav Chaudhary
Rahul Khanna (OWNER)
Rohan Sachdeva
Simran Sethi
Vaneet Ummat

M R Boundary Bashers
Abhishek Arora
Amit Chaudhry
Chander Shekhar Jain
Daevinder Singh
Munish Malhotra
Pankaj Pahwa
Praveen Jaggi
Raj Kumar
Randeep Singh
Ripan Goyal (OWNER)
Varun Talwar
Vipul Bahl
Vishal Gupta

Western Living UCC Legends
Abhi Marjara
Ankit Arora
Babar Kailay
Bhupinder Singh
Deepankar Jain
Japkirat Banga
Manan Berry
Ritesh Takkar
Sanjeev Bhalla
Shavinder Singh (OWNER)
Shrey Sayal
Varun Sharma
Vishesh Bansal (Guest)`;

const money = (lakh) => (lakh == null ? '—' : lakh >= 100 ? `₹${(lakh / 100).toFixed(2)} Cr` : `₹${lakh} L`);
const day = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
const errOf = (e) => e?.response?.data?.error || e?.message || 'Something went wrong';

function Card({ title, right, children, accent }) {
  return (
    <div style={{ background: 'white', border: `1px solid ${BORDER}`, borderTop: accent ? `3px solid ${accent}` : `1px solid ${BORDER}`,
                  borderRadius: 12, padding: 14, marginBottom: 14 }}>
      {(title || right) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
          <div style={{ ...H, fontSize: '1.05rem' }}>{title}</div>{right}
        </div>
      )}
      {children}
    </div>
  );
}
const btn = (kind = 'navy') => ({
  padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '0.82rem',
  border: kind === 'ghost' ? `1px solid ${NAVY}` : 'none',
  background: kind === 'navy' ? NAVY : kind === 'gold' ? GOLD : kind === 'red' ? '#FEE2E2' : 'white',
  color: kind === 'navy' ? 'white' : kind === 'gold' ? '#0F1E38' : kind === 'red' ? '#B91C1C' : NAVY,
});
const label = { fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, marginBottom: 3 };

// ── Rules ─────────────────────────────────────────────────────
function RulesCard({ st, onSave, busy }) {
  const a = st.auction;
  const [f, setF] = useState(null);
  useEffect(() => {
    setF({
      purse_cr: (a.purse_lakh / 100).toString(), base: String(a.base_lakh),
      ladder: (a.ladder || []).join(', '), after: String(a.ladder_after_lakh),
      ret: (a.retention_pcts || []).join(', '), prev_year: a.prev_year ? String(a.prev_year) : '', rounds: String(a.rounds_max),
    });
  }, [a]);
  if (!f) return null;
  const locked = a.status !== 'setup';
  const save = () => onSave({
    purse_lakh: Math.round(parseFloat(f.purse_cr) * 100),
    base_lakh: parseInt(f.base, 10),
    ladder: f.ladder.split(/[,\s]+/).filter(Boolean).map(x => parseInt(x, 10)),
    ladder_after_lakh: parseInt(f.after, 10),
    retention_pcts: f.ret.split(/[,\s]+/).filter(Boolean).map(Number),
    prev_year: f.prev_year ? parseInt(f.prev_year, 10) : null,
    rounds_max: parseInt(f.rounds, 10),
  });
  const inp = (k, w) => (
    <input className="form-control" disabled={locked} value={f[k]} style={{ width: w || '100%' }}
           onChange={e => setF({ ...f, [k]: e.target.value })} />
  );
  const pcts = f.ret.split(/[,\s]+/).filter(Boolean).map(Number);
  const purse = parseFloat(f.purse_cr) * 100 || 0;
  return (
    <Card title="Rules" right={!locked && <button style={btn()} disabled={busy} onClick={save}>Save rules</button>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <div><div style={label}>Purse (₹ Cr)</div>{inp('purse_cr')}</div>
        <div><div style={label}>Base price (₹ L)</div>{inp('base')}</div>
        <div><div style={label}>Retention cuts (%)</div>{inp('ret')}</div>
        <div><div style={label}>Last year's squads</div>{inp('prev_year')}</div>
        <div><div style={label}>Rounds</div>{inp('rounds')}</div>
        <div><div style={label}>After ladder +(₹ L)</div>{inp('after')}</div>
      </div>
      <div style={{ ...label, marginTop: 10 }}>Bid ladder (₹ L)</div>
      {inp('ladder')}
      <div style={{ fontSize: '0.75rem', color: MUTED, marginTop: 8 }}>
        Purse after retentions: {[0, 1, 2, 3].slice(0, pcts.length + 1).map(n => {
          const cut = pcts.slice(0, n).reduce((s, v) => s + v, 0);
          return <span key={n} style={{ marginRight: 12 }}><b style={{ color: NAVY }}>{n}</b> → {money(Math.round(purse * (100 - cut) / 100))}</span>;
        })}
      </div>
      {locked && <div style={{ fontSize: '0.75rem', color: '#B45309', marginTop: 6 }}>The auction has started — rules are locked.</div>}
    </Card>
  );
}

// ── Teams ─────────────────────────────────────────────────────
function TeamsCard({ st, members, onTeam, onPlayer, busy }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 14 }}>
      {st.teams.map(t => {
        const squad = st.players.filter(p => p.team_id === t.id && ['owner', 'retained', 'sold'].includes(p.status));
        return (
          <div key={t.id} style={{ background: 'white', border: `1px solid ${BORDER}`, borderTop: `4px solid ${t.color || NAVY}`, borderRadius: 12, padding: 12 }}>
            <input className="form-control" defaultValue={t.name} disabled={busy}
                   onBlur={e => e.target.value.trim() !== t.name && onTeam(t.id, { name: e.target.value })}
                   style={{ ...H, fontSize: '1rem', color: t.color || NAVY, border: 'none', padding: 0, marginBottom: 6 }} />
            <div style={label}>Owner (in the team at ₹0)</div>
            <select className="form-control" value={t.owner_member_id || ''} disabled={busy || st.auction.status !== 'setup'}
                    onChange={e => onTeam(t.id, { owner_member_id: e.target.value || null })}>
              <option value="">— choose —</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
            </select>
            <div style={{ ...label, marginTop: 8 }}>Captain</div>
            <select className="form-control" value={t.captain_member_id || ''} disabled={busy}
                    onChange={e => onTeam(t.id, { captain_member_id: e.target.value || null })}>
              <option value="">— choose —</option>
              {squad.map(p => <option key={p.member_id} value={p.member_id}>{p.display_name}{p.status === 'owner' ? ' (owner)' : ''}</option>)}
            </select>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: '0.8rem' }}>
              <span style={{ color: MUTED }}>Purse</span><b style={{ color: NAVY }}>{money(t.purse_start_lakh)}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
              <span style={{ color: MUTED }}>Squad</span><b style={{ color: NAVY }}>{t.squad_count}{st.pool.squad_size ? ` / ${st.pool.squad_size}` : ''}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
              <span style={{ color: MUTED }}>Max bid now</span><b style={{ color: NAVY }}>{money(t.max_bid_lakh)}</b>
            </div>
            <div style={{ ...label, marginTop: 10 }}>Retained ({t.retained.length}/{(st.auction.retention_pcts || []).length})</div>
            {t.retained.length === 0 && <div style={{ fontSize: '0.78rem', color: MUTED }}>None — retain from the pool below.</div>}
            {t.retained.map(r => (
              <div key={r.player_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem', padding: '3px 0' }}>
                <span>{r.order}. {r.name}</span>
                {st.auction.status === 'setup' && (
                  <button style={{ ...btn('red'), padding: '2px 8px' }} disabled={busy} onClick={() => onPlayer(r.player_id, { action: 'unretain' })}>Remove</button>
                )}
              </div>
            ))}
            <div style={{ fontSize: '0.7rem', color: MUTED, marginTop: 8 }}>
              Last year: {t.prev_team_name || '—'} · RTM card {t.rtm_used ? 'used' : 'available'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Last year's squads ────────────────────────────────────────
function PrevSquadsCard({ year, allMembers }) {
  const [saved, setSaved] = useState(null);
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    if (!year) return;
    api.get(`/auction/prev-squads/${year}`).then(r => setSaved(r.data.rows)).catch(() => setSaved([]));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  function parseText(t) {
    // Team name on its own line, players below, a blank line between teams.
    const teams = [];
    for (const block of t.split(/\n\s*\n/)) {
      const lines = block.split('\n').map(s => s.trim()).filter(Boolean);
      if (lines.length < 2) continue;
      teams.push({ team_name: lines[0], names: lines.slice(1) });
    }
    return teams;
  }
  async function match() {
    setBusy(true); setMsg('');
    try {
      const r = await api.post(`/auction/prev-squads/${year}/match`, { teams: parseText(text) });
      setRows(r.data.rows);
    } catch (e) { setMsg(errOf(e)); } finally { setBusy(false); }
  }
  async function save() {
    setBusy(true); setMsg('');
    try {
      const r = await api.put(`/auction/prev-squads/${year}`, { rows: rows.map(x => ({
        team_name: x.team_name, listed_name: x.listed_name, member_id: x.member_id || null, is_owner: x.is_owner, is_guest: x.is_guest })) });
      setMsg(`Saved ${r.data.saved} names${r.data.unmatched ? ` (${r.data.unmatched} without a member)` : ''}.`);
      setRows(null); setText(''); load();
    } catch (e) { setMsg(errOf(e)); } finally { setBusy(false); }
  }

  if (!year) return <Card title="Last year's squads"><div style={{ color: MUTED, fontSize: '0.85rem' }}>Set "Last year's squads" in the rules first.</div></Card>;
  const pending = rows ? rows.filter(r => !r.member_id).length : 0;
  const byTeam = (list) => list.reduce((m, r) => { (m[r.team_name] = m[r.team_name] || []).push(r); return m; }, {});

  return (
    <Card title={`${year} squads (for retention & RTM)`}
          right={!rows && <button style={btn('ghost')} onClick={() => setText(text || (year === 2025 ? SQUADS_2025 : ''))}>{saved?.length ? 'Load again' : 'Load the list'}</button>}>
      {saved && saved.length > 0 && !rows && !text && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {Object.entries(byTeam(saved)).map(([team, list]) => (
            <div key={team}>
              <div style={{ ...H, fontSize: '0.9rem' }}>{team}</div>
              {list.map(r => (
                <div key={r.id} style={{ fontSize: '0.8rem', color: r.member_id ? '#111827' : '#B91C1C' }}>
                  {r.display_name || r.listed_name}{r.is_owner ? ' (owner)' : ''}{r.is_guest ? ' (guest)' : ''}{!r.member_id ? ' — not matched' : ''}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {saved && saved.length === 0 && !text && !rows && <div style={{ color: MUTED, fontSize: '0.85rem' }}>Not loaded yet.</div>}
      {text !== '' && !rows && (
        <>
          <div style={{ fontSize: '0.75rem', color: MUTED, marginBottom: 6 }}>Team name on its own line, its players below, a blank line between teams.</div>
          <textarea className="form-control" rows={14} value={text} onChange={e => setText(e.target.value)} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button style={btn()} disabled={busy} onClick={match}>Match names to members</button>
            <button style={btn('ghost')} onClick={() => setText('')}>Cancel</button>
          </div>
        </>
      )}
      {rows && (
        <>
          <div style={{ fontSize: '0.8rem', color: pending ? '#B45309' : '#15803D', marginBottom: 8 }}>
            {pending ? `${pending} name${pending === 1 ? '' : 's'} need you to choose the member (e.g. the two Abhishek Aroras).` : 'Every name is matched. Check and save.'}
          </div>
          {Object.entries(byTeam(rows)).map(([team, list]) => (
            <div key={team} style={{ marginBottom: 10 }}>
              <div style={{ ...H, fontSize: '0.9rem' }}>{team}</div>
              {list.map(r => {
                const idx = rows.indexOf(r);
                const opts = r.candidates.length ? r.candidates : allMembers.map(m => ({ id: m.id, display_name: m.display_name, ucc_id: m.ucc_id }));
                return (
                  <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                    <span style={{ width: 190, fontSize: '0.8rem' }}>{r.listed_name}</span>
                    <select className="form-control" value={r.member_id || ''} style={{ flex: 1, borderColor: r.member_id ? BORDER : '#B45309' }}
                            onChange={e => setRows(rows.map((x, i) => (i === idx ? { ...x, member_id: e.target.value || null } : x)))}>
                      <option value="">— not a member / skip —</option>
                      {opts.map(c => <option key={c.id} value={c.id}>{c.display_name} ({c.ucc_id})</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn()} disabled={busy} onClick={save}>Save {year} squads</button>
            <button style={btn('ghost')} onClick={() => setRows(null)}>Back</button>
          </div>
        </>
      )}
      {msg && <div style={{ fontSize: '0.8rem', color: NAVY, marginTop: 8 }}>{msg}</div>}
    </Card>
  );
}

// ── Guests ────────────────────────────────────────────────────
function GuestForm({ st, guests, onAdd, busy, onClose }) {
  const [f, setF] = useState({ member_id: '', display_name: '', match_ids: [], role: '', bat_hand: '', bowl_arm: '', bowl_type: '' });
  const toggle = (id) => setF({ ...f, match_ids: f.match_ids.includes(id) ? f.match_ids.filter(x => x !== id) : [...f.match_ids, id] });
  const sel = (k, opts) => (
    <select className="form-control" value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })}>
      <option value="">—</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  const inPool = new Set(st.players.map(p => p.member_id));
  return (
    <div style={{ border: `1.5px solid ${GOLD_D}`, background: '#FFFDF0', borderRadius: 10, padding: 12, marginBottom: 12 }}>
      <div style={{ ...H, marginBottom: 8 }}>Add a guest</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
        <div><div style={label}>Guest who has played before</div>
          <select className="form-control" value={f.member_id} onChange={e => setF({ ...f, member_id: e.target.value, display_name: '' })}>
            <option value="">— new guest —</option>
            {guests.filter(g => !inPool.has(g.id)).map(g => <option key={g.id} value={g.id}>{g.display_name}</option>)}
          </select></div>
        {!f.member_id && <div><div style={label}>New guest's name</div>
          <input className="form-control" value={f.display_name} onChange={e => setF({ ...f, display_name: e.target.value })} /></div>}
        <div><div style={label}>Plays as</div>{sel('role', [['batsman', 'Batsman'], ['bowler', 'Bowler'], ['keeper', 'Wicket keeper'], ['allrounder', 'All-rounder']])}</div>
        <div><div style={label}>Bats</div>{sel('bat_hand', [['RHB', 'Right hand (RHB)'], ['LHB', 'Left hand (LHB)']])}</div>
        <div><div style={label}>Bowling arm</div>{sel('bowl_arm', [['right', 'Right arm'], ['left', 'Left arm']])}</div>
        <div><div style={label}>Bowling</div>{sel('bowl_type', [['medium', 'Pace — medium'], ['fast', 'Pace — fast'], ['off', 'Spin — off'], ['leg', 'Spin — leg'], ['none', "Doesn't bowl"]])}</div>
      </div>
      <div style={{ ...label, marginTop: 10 }}>Dates he can play</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {st.fixtures.map(fx => (
          <button key={fx.id} onClick={() => toggle(fx.id)}
                  style={{ ...btn(f.match_ids.includes(fx.id) ? 'navy' : 'ghost'), padding: '5px 10px' }}>{day(fx.day)}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button style={btn('gold')} disabled={busy} onClick={() => onAdd(f)}>Add guest</button>
        <button style={btn('ghost')} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

// ── After the auction: fixtures + squads into the series ──────
function SeriesCard({ st, busy, onFixture, onSend }) {
  const a = st.auction;
  const done = a.status === 'done';
  const teamName = (id) => st.teams.find(t => t.id === id)?.short_name || '—';
  return (
    <Card title="Series matches" accent={GOLD}
          right={done && <button style={btn('gold')} disabled={busy} onClick={onSend}>{st.series_squad_rows ? '↻ Send squads again' : 'Send squads to the series'}</button>}>
      <div style={{ fontSize: '0.78rem', color: MUTED, marginBottom: 10, lineHeight: 1.5 }}>
        Choose which two teams play on each date (you can fill in later dates once earlier results are known).
        {done
          ? <> After the auction, <b>Send squads to the series</b> makes every team's squad its series squad under its captain,
              sets each match's captains, and sets the series rules: minimum 5 bowlers, last man batting not allowed.
              {st.series_squad_rows ? <b style={{ color: '#15803D' }}> ✓ {st.series_squad_rows} players are in the series squads.</b> : null}</>
          : <> The squads go to the series once the auction is finished.</>}
      </div>
      {st.fixtures.map((f, i) => (
        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderTop: i ? `1px solid ${BORDER}` : 'none', flexWrap: 'wrap' }}>
          <div style={{ width: 110, fontWeight: 700, color: NAVY, fontSize: '0.85rem' }}>{day(f.day)}</div>
          {['team_a_id', 'team_b_id'].map((k, j) => (
            <React.Fragment key={k}>
              {j === 1 && <span style={{ color: MUTED, fontSize: '0.8rem' }}>vs</span>}
              <select className="form-control" style={{ width: 170 }} value={f[k] || ''} disabled={busy || f.started}
                      onChange={e => onFixture(f.id, { team_a_id: f.team_a_id, team_b_id: f.team_b_id, [k]: e.target.value || null })}>
                <option value="">— team —</option>
                {st.teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </React.Fragment>
          ))}
          <span style={{ fontSize: '0.72rem', color: MUTED }}>
            {f.started ? 'played / in progress' : f.capt1_name && f.team_a_id ? `Captains: ${f.capt1_name} · ${f.capt2_name}` : f.team_a_id && f.team_b_id ? `${teamName(f.team_a_id)} v ${teamName(f.team_b_id)}` : ''}
          </span>
        </div>
      ))}
    </Card>
  );
}

// ── Pool ──────────────────────────────────────────────────────
function PoolCard({ st, guests, onRefresh, onPlayer, onAddGuest, busy }) {
  const [showGuest, setShowGuest] = useState(false);
  const [filter, setFilter] = useState('in');
  const pool = st.pool;
  const setup = st.auction.status === 'setup';
  const teamById = useMemo(() => new Map(st.teams.map(t => [t.id, t])), [st.teams]);
  const teamForPrev = (prev) => st.teams.find(t => (t.prev_team_name || t.name).toLowerCase() === String(prev || '').toLowerCase());
  const list = st.players
    .filter(p => filter === 'all' ? true : filter === 'guests' ? p.is_guest : filter === 'out' ? p.status === 'out' : p.status !== 'out')
    .sort((a, b) => (a.status === 'owner' ? -2 : a.status === 'retained' ? -1 : 0) - (b.status === 'owner' ? -2 : b.status === 'retained' ? -1 : 0)
                   || a.display_name.localeCompare(b.display_name));
  const roleShort = { batsman: 'BAT', bowler: 'BOWL', keeper: 'WK', allrounder: 'AR' };

  return (
    <Card title={`Player pool · ${pool.count}`}
          right={setup && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={btn('ghost')} disabled={busy} onClick={onRefresh}>↻ From availability</button>
              <button style={btn('gold')} onClick={() => setShowGuest(true)}>+ Guest</button>
            </div>)}>
      <div style={{ padding: '10px 12px', borderRadius: 10, marginBottom: 12, fontSize: '0.85rem',
                    background: pool.divisible ? '#DCFCE7' : '#FEF3C7', color: pool.divisible ? '#15803D' : '#B45309' }}>
        {pool.count === 0
          ? 'No players yet — press "From availability" once members have replied.'
          : pool.divisible
            ? <>✓ <b>{pool.squad_size} a side</b> — {pool.members} members + {pool.guests} guest{pool.guests === 1 ? '' : 's'} = {pool.count}, equal squads. Owners and retained players count in the {pool.squad_size}.</>
            : <>{pool.count} players doesn't divide into 3 equal squads. <b>Add {pool.add_guests} guest{pool.add_guests === 1 ? '' : 's'}</b>
                {' '}(→ {pool.count + pool.add_guests}, {(pool.count + pool.add_guests) / 3} a side) or <b>leave {pool.leave_out} out</b>
                {' '}(→ {pool.count - pool.leave_out}, {(pool.count - pool.leave_out) / 3} a side). Bidding can't start until it does.</>}
      </div>
      {showGuest && <GuestForm st={st} guests={guests} busy={busy} onClose={() => setShowGuest(false)}
                               onAdd={async (f) => { if (await onAddGuest(f)) setShowGuest(false); }} />}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {[['in', 'In the pool'], ['guests', 'Guests'], ['out', 'Left out'], ['all', 'All']].map(([k, l]) => (
          <button key={k} style={{ ...btn(filter === k ? 'navy' : 'ghost'), padding: '4px 10px' }} onClick={() => setFilter(k)}>{l}</button>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ background: NAVY, color: 'white', textAlign: 'left' }}>
              <th style={{ padding: '7px 8px' }}>Player</th>
              <th style={{ padding: '7px 6px' }}>Dates</th>
              <th style={{ padding: '7px 6px' }}>Plays as</th>
              <th style={{ padding: '7px 6px' }}>Last year</th>
              <th style={{ padding: '7px 6px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {list.map(p => {
              const team = p.team_id ? teamById.get(p.team_id) : null;
              const retainTeam = teamForPrev(p.prev_team);
              return (
                <tr key={p.id} style={{ borderBottom: `1px solid ${BORDER}`, opacity: p.status === 'out' ? 0.5 : 1 }}>
                  <td style={{ padding: '6px 8px', fontWeight: 600, color: NAVY, whiteSpace: 'nowrap' }}>
                    {p.display_name}
                    {p.is_guest && <span style={{ marginLeft: 6, fontSize: '0.62rem', background: '#FBF5DC', color: GOLD_D, padding: '1px 6px', borderRadius: 6, fontWeight: 800 }}>GUEST</span>}
                    {p.no_bowling && <span title="Said No bowling" style={{ marginLeft: 4, fontSize: '0.62rem', background: '#FEF3C7', color: '#B45309', padding: '1px 5px', borderRadius: 6 }}>No bowl</span>}
                    {p.no_keeping && <span title="No keeping" style={{ marginLeft: 4, fontSize: '0.62rem', background: '#FEF3C7', color: '#B45309', padding: '1px 5px', borderRadius: 6 }}>No keep</span>}
                  </td>
                  <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
                    {p.dates.map(d => (
                      <span key={d.match_id} title={day(d.day)} style={{ display: 'inline-block', width: 22, textAlign: 'center', marginRight: 2, borderRadius: 5,
                        fontSize: '0.62rem', fontWeight: 800, padding: '1px 0', background: d.yes ? '#15803D' : '#E5E7EB', color: d.yes ? 'white' : MUTED }}>
                        {new Date(d.day + 'T00:00:00').getDate()}
                      </span>
                    ))}
                  </td>
                  <td style={{ padding: '6px', whiteSpace: 'nowrap', color: MUTED }}>
                    {setup ? (
                      <select value={p.role || ''} onChange={e => onPlayer(p.id, { role: e.target.value || null })} style={{ fontSize: '0.75rem' }}>
                        <option value="">—</option>
                        {Object.entries(roleShort).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    ) : (roleShort[p.role] || '—')}
                    {' '}{p.bat_hand || ''}{p.bowl_type && p.bowl_type !== 'none' ? ` · ${p.bowl_arm === 'left' ? 'L' : 'R'}-${p.bowl_type}` : ''}
                  </td>
                  <td style={{ padding: '6px', color: MUTED, whiteSpace: 'nowrap' }}>{p.prev_team || '—'}</td>
                  <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
                    {p.status === 'owner' && <b style={{ color: team?.color || NAVY }}>Owner · {team?.short_name}</b>}
                    {p.status === 'retained' && <b style={{ color: team?.color || NAVY }}>Retained #{p.retain_order} · {team?.short_name}</b>}
                    {p.status === 'pool' && setup && (
                      <span style={{ display: 'inline-flex', gap: 4 }}>
                        {retainTeam && (retainTeam.retained.length < (st.auction.retention_pcts || []).length) && (
                          <button style={{ ...btn('ghost'), padding: '2px 8px', borderColor: retainTeam.color, color: retainTeam.color }} disabled={busy}
                                  onClick={() => onPlayer(p.id, { action: 'retain', team_id: retainTeam.id })}>Retain · {retainTeam.short_name}</button>
                        )}
                        <button style={{ padding: '2px 6px', border: 'none', background: 'none', color: MUTED, textDecoration: 'underline', cursor: 'pointer', fontSize: '0.75rem' }}
                                disabled={busy} onClick={() => onPlayer(p.id, { action: 'out' })}>Leave out</button>
                      </span>
                    )}
                    {p.status === 'pool' && !setup && <span style={{ color: MUTED }}>In the pool</span>}
                    {p.status === 'out' && setup && <button style={{ ...btn('ghost'), padding: '2px 8px' }} disabled={busy} onClick={() => onPlayer(p.id, { action: 'in' })}>Put back</button>}
                    {['sold', 'unsold'].includes(p.status) && <span>{p.status === 'sold' ? `Sold · ${team?.short_name} · ${money(p.price_lakh)}` : 'Unsold'}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: '0.72rem', color: MUTED, marginTop: 8, lineHeight: 1.5 }}>
        Dates: green = available. A player can be retained only by the franchise he played for last year (up to {(st.auction.retention_pcts || []).length} per team).
        "Leave out" takes a player out of this auction, e.g. to make the numbers divide by 3.
      </div>
    </Card>
  );
}

export default function AdminAuctionPage() {
  const [seriesList, setSeriesList] = useState([]);
  const [seriesId, setSeriesId] = useState('');
  const [st, setSt] = useState(null);
  const [members, setMembers] = useState([]);
  const [guests, setGuests] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get('/series?all=1').then(r => setSeriesList(r.data.series || [])).catch(() => {});
    api.get('/members?includeObservers=1').then(r => {
      const all = r.data.members || [];
      setMembers(all.filter(m => !m.is_observer));
      setGuests(all.filter(m => m.is_observer));
    }).catch(() => {});
  }, []);

  const load = useCallback(async (sid) => {
    if (!sid) { setSt(null); return; }
    try { const r = await api.get(`/auction/by-series/${sid}`); setSt(r.data); }
    catch (e) { setMsg({ err: true, text: errOf(e) }); }
  }, []);
  useEffect(() => { load(seriesId); }, [seriesId, load]);

  const run = async (fn, okText) => {
    setBusy(true); setMsg(null);
    try { const r = await fn(); if (r?.data?.auction) setSt(r.data); if (okText) setMsg({ text: typeof okText === 'function' ? okText(r) : okText }); return true; }
    catch (e) { setMsg({ err: true, text: errOf(e) }); return false; }
    finally { setBusy(false); }
  };
  const a = st?.auction;

  return (
    <div style={{ paddingBottom: 40, background: '#F5F6F8', minHeight: '100%' }}>
      <div style={{ background: 'white', borderBottom: `1px solid ${BORDER}`, padding: '16px 16px 14px' }}>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: GOLD_D }}>Admin · Super Series</div>
        <div style={{ ...H, fontSize: '1.4rem' }}>🔨 Player Auction — Setup</div>
      </div>
      <div style={{ padding: 16, maxWidth: 1100, margin: '0 auto' }}>
        <Card>
          <div style={label}>Series</div>
          <select className="form-control" value={seriesId} onChange={e => setSeriesId(e.target.value)}>
            <option value="">Choose a series…</option>
            {seriesList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.year}){s.total_fixtures != null ? ` · ${s.total_fixtures} dates` : ''}</option>)}
          </select>
        </Card>

        {msg && (
          <div style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem',
                        background: msg.err ? '#FEE2E2' : '#DCFCE7', color: msg.err ? '#B91C1C' : '#15803D' }}>{msg.text}</div>
        )}

        {st && !a && (
          <Card title={st.series?.name}>
            <div style={{ fontSize: '0.85rem', color: MUTED, marginBottom: 10 }}>
              This series has no auction yet. Making it an auction series adds the 3 franchises with their owners and the charter rules
              (₹10 Cr purse, ₹5 L base, retention 25 / 17 / 13%). Everything can be changed before the auction.
            </div>
            <button style={btn('gold')} disabled={busy} onClick={() => run(() => api.post(`/auction/by-series/${seriesId}`), 'Auction series created.')}>
              Make this an auction series
            </button>
          </Card>
        )}

        {a && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <a href={`/admin/auction/live/${a.id}`} style={{ ...btn('navy'), textDecoration: 'none' }}>🎛 Live auction control</a>
              <a href="/auction/live" target="_blank" rel="noreferrer" style={{ ...btn('ghost'), textDecoration: 'none' }}>📺 Big screen</a>
              {a.status !== 'setup' && (
                <button style={{ ...btn('red'), marginLeft: 'auto' }} disabled={busy}
                        onClick={() => window.confirm('Reset the auction?\n\nEvery sale, bid and Right To Match is undone and the auction goes back to setup. Owners, retentions, guests and the pool are kept. Use this after a rehearsal.')
                          && run(() => api.post(`/auction/${a.id}/live/reset`).then(() => api.get(`/auction/by-series/${seriesId}`)), 'Auction reset to setup.')}>
                  Reset auction
                </button>
              )}
            </div>
            <div style={{ fontSize: '0.8rem', color: MUTED, marginBottom: 10 }}>
              {st.series.name} · {st.fixtures.length} date{st.fixtures.length === 1 ? '' : 's'}: {st.fixtures.map(f => day(f.day)).join(', ') || '—'} ·
              status <b style={{ color: NAVY }}>{a.status}</b>
            </div>
            <RulesCard st={st} busy={busy} onSave={(body) => run(() => api.patch(`/auction/${a.id}`, body), 'Rules saved.')} />
            <TeamsCard st={st} members={members} busy={busy}
                       onTeam={(tid, body) => run(() => api.patch(`/auction/${a.id}/teams/${tid}`, body))}
                       onPlayer={(pid, body) => run(() => api.patch(`/auction/${a.id}/players/${pid}`, body))} />
            <SeriesCard st={st} busy={busy}
                        onFixture={(mid, body) => run(() => api.put(`/auction/${a.id}/fixtures/${mid}`, body))}
                        onSend={() => window.confirm('Send every team\'s squad to the series? Captains pick their playing team for each date from these squads.')
                          && run(() => api.post(`/auction/${a.id}/send-to-series`),
                                 (r) => `Sent — ${r.data.sent.players} players in the series squads, ${r.data.sent.fixtures} match${r.data.sent.fixtures === 1 ? '' : 'es'} with captains set.`)} />
            <PrevSquadsCard year={a.prev_year} allMembers={[...members, ...guests]} />
            <PoolCard st={st} guests={guests} busy={busy}
                      onRefresh={() => run(() => api.post(`/auction/${a.id}/pool/refresh`),
                        (r) => `Pool updated — ${r.data.refreshed.added} added, ${r.data.refreshed.dropped} removed (no YES for any date).`)}
                      onPlayer={(pid, body) => run(() => api.patch(`/auction/${a.id}/players/${pid}`, body))}
                      onAddGuest={(f) => run(() => api.post(`/auction/${a.id}/guests`, {
                        member_id: f.member_id || undefined, display_name: f.member_id ? undefined : f.display_name,
                        match_ids: f.match_ids, role: f.role || null, bat_hand: f.bat_hand || null,
                        bowl_arm: f.bowl_arm || null, bowl_type: f.bowl_type || null,
                      }), 'Guest added.')} />
          </>
        )}
      </div>
    </div>
  );
}
